import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, ArrowLeft } from 'lucide-react';

export default function AuthPage() {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const githubEnabled = true;

  const formatError = (detail) => {
    if (!detail) return 'Authentication failed';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => e?.msg || '').filter(Boolean).join(' ') || 'Invalid input';
    if (detail?.msg) return detail.msg;
    return 'Authentication failed';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      let res;
      if (mode === 'register') {
        res = await authAPI.register({ email, password, name });
      } else {
        res = await authAPI.login({ email, password });
      }
      login(res.data, res.data.token);
      navigate('/dashboard');
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#020202] grid-bg flex items-center justify-center p-6" data-testid="auth-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-[#999] hover:text-[#FF7A3D] transition-colors mb-8 font-mono text-sm" data-testid="back-to-home">
          <ArrowLeft className="w-4 h-4" />
          BACK TO BASE
        </Link>

        <div className="card-cyber p-8 rounded-sm">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="w-8 h-8 text-[#FF7A3D]" />
            <div>
              <h1 className="font-heading text-2xl font-bold text-white tracking-tight">DEFENCES.AI</h1>
              <p className="font-mono text-xs text-[#999] tracking-widest uppercase">
                {mode === 'login' ? 'ACCESS TERMINAL' : 'NEW OPERATIVE'}
              </p>
            </div>
          </div>

          <button
            onClick={handleGoogleAuth}
            className="w-full flex items-center justify-center gap-3 bg-white/5 border border-white/10 hover:border-[#FF7A3D]/30 text-white py-3 rounded-sm transition-all duration-300 mb-6 font-body font-medium"
            data-testid="google-auth-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          {githubEnabled && (
            <div
              className="w-full flex items-center justify-center gap-3 bg-white/[0.02] border border-white/5 text-[#888] py-3 rounded-sm mb-6 font-body font-medium cursor-not-allowed select-none"
              data-testid="github-auth-btn"
              title="GitHub login coming soon"
            >
              <svg className="w-5 h-5 opacity-50" viewBox="0 0 24 24" fill="#888"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/></svg>
              <span>Continue with GitHub</span>
              <span className="font-mono text-[9px] tracking-widest text-[#00E5C1] border border-[#00E5C1]/30 rounded-sm px-1.5 py-0.5">SOON</span>
            </div>
          )}

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="font-mono text-xs text-[#999] tracking-widest">OR</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === 'register' && (
              <div className="space-y-2">
                <Label className="font-mono text-xs text-[#999] tracking-widest uppercase">Operative Name</Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-black border-white/10 focus:border-[#FF7A3D] font-mono placeholder:text-[#555]"
                  placeholder=">_ Enter name"
                  required
                  data-testid="name-input"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label className="font-mono text-xs text-[#999] tracking-widest uppercase">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-black border-white/10 focus:border-[#FF7A3D] font-mono placeholder:text-[#555]"
                placeholder=">_ Enter email"
                required
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-xs text-[#999] tracking-widest uppercase">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-black border-white/10 focus:border-[#FF7A3D] font-mono placeholder:text-[#555]"
                placeholder=">_ Enter password"
                required
                data-testid="password-input"
              />
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-3" data-testid="auth-error">
                <p className="font-mono text-xs text-red-400">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FF7A3D] text-black font-heading font-bold uppercase tracking-widest hover:bg-[#FF7A3D]/90 hover:shadow-[0_0_15px_rgba(255,122,61,0.5)] transition-all rounded-sm h-11"
              data-testid="auth-submit-btn"
            >
              {loading ? 'PROCESSING...' : mode === 'login' ? 'ACCESS GRANTED' : 'INITIALIZE ACCOUNT'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
              className="font-mono text-xs text-[#999] hover:text-[#FF7A3D] transition-colors"
              data-testid="toggle-auth-mode"
            >
              {mode === 'login' ? 'NEW OPERATIVE? CREATE ACCOUNT' : 'EXISTING OPERATIVE? LOGIN'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
